import chunk from 'lodash/chunk';
import { memo, useEffect, useRef, useState } from 'react';
import { Animated } from 'react-native';
import safeAwait from 'safe-await';

import { MktType } from '../../../constants/MktType';
import { checkItemViewed } from '../../../context/devLogFirehose';
import ScrollController from '../../../elements/ScrollController';
import { translate } from '../../../i18n';
import { AdditionalCategory } from '../../../types/Categories';
import { CatItem } from '../../../types/CatsResponse';
import Configs from '../../../utils/configs';
import { getRemoteValue } from '../../../utils/packages/firebase';
import FlatListWithImpression from '../../FlatListWithImpression';
import { categoryProducer } from '../../FlatListWithImpression/utils';
import CategoryItem from '../CategoryItem';
import { S } from './styles';

interface Props {
  categories: CatItem[];
  isCatFetching: boolean;
  isLoading: boolean;
}

function keyExtractor(items: CatItem[]) {
  return items[0].id;
}

const LAYOUT_NAME = 'index_category';

interface AdditionalCatsState {
  isFetching: boolean;
  first: AdditionalCategory[];
  last: AdditionalCategory[];
}

const MoreEntries: Record<
  'categoriesIndex' | 'newArrival' | 'fastDelivery',
  AdditionalCategory
> = {
  categoriesIndex: {
    get name() {
      return translate('categories-index');
    },
    href: `https://${Configs.MHOST}/categories`,
    id: 'categories-index',
    imageSource: require('./assets/ic_all_v2.png'),
  },
  newArrival: {
    get name() {
      return translate('categories-new-arrivals');
    },
    href: `https://${Configs.MHOST}/new_arrivals`,
    id: 'categories-new-arrivals',
    imageSource: require('./assets/img_newarrivals_cate_entrance.png'),
  },
  fastDelivery: {
    get name() {
      return translate('fast-delivery');
    },
    href: 'https://www.verybrand.cc',
    id: 'fast-delivery',
    imageSource: require('./assets/spotArea.png'),
  },
};

function CategoriesList(props: Props) {
  const { categories, isLoading, isCatFetching } = props;
  const [scrollOffset] = useState(() => new Animated.Value(0));
  const [additionalCats, setAdditionalCats] = useState<AdditionalCatsState>({
    isFetching: true,
    first: [],
    last: [],
  });

  const impressionList = useRef<typeof FlatListWithImpression>();

  const renderCategoryItem = ({ item, index }) => {
    return (
      <CategoryItem
        key={item}
        categoryPair={item}
        index={index}
        layoutName={LAYOUT_NAME}
        shouldDisplayImpressionForDebug={checkItemViewed(
          impressionList.current?.accumulatedItems,
          item[0].id
        )}
      />
    );
  };

  useEffect(() => {
    const getFastDeliveryEnabled = safeAwait(
      getRemoteValue('category_fast_delivery')
    );

    Promise.all([getFastDeliveryEnabled]).then(
      ([[_err, fastDeliveryEnabled]]) => {
        const dataFirst: AdditionalCategory[] = [];
        const dataLast: AdditionalCategory[] = [];

        if (fastDeliveryEnabled && fastDeliveryEnabled.asBoolean()) {
          dataLast.push(MoreEntries.fastDelivery);
        }

        setAdditionalCats({
          isFetching: false,
          first: dataFirst,
          last: dataLast,
        });
      }
    );
  }, []);

  const isCatLoading = isLoading || isCatFetching;
  const isCatExisting =
    categories.length > 0 ||
    additionalCats.first.length > 0 ||
    additionalCats.last.length > 0;
  const moreFirstEntries = isCatExisting ? [MoreEntries.newArrival] : [];
  const moreLastEntries = isCatExisting ? [MoreEntries.categoriesIndex] : [];
  const categoriesToRender = isCatLoading
    ? Array(9)
        .fill('category-blank')
        .map((key, index) => ({ id: `${key}${index}` } as CatItem))
    : [
        ...moreFirstEntries,
        ...additionalCats.first,
        ...categories,
        ...moreLastEntries,
        ...additionalCats.last,
      ];
  const categoryPairList = chunk(categoriesToRender, 2);

  if (categoriesToRender.length === 0) {
    return null;
  }

  return (
    <S.ListContainer>
      <S.List
        testID="categoryList"
        ref={impressionList}
        data={categoryPairList}
        renderItem={renderCategoryItem}
        keyExtractor={keyExtractor}
        mktType={MktType.Icon}
        layoutName={LAYOUT_NAME}
        horizontal
        showsHorizontalScrollIndicator={false}
        dataProducer={categoryProducer}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { x: scrollOffset } } }],
          {
            useNativeDriver: false,
          }
        )}
      />
      <ScrollController
        scrollRef={impressionList}
        scrollOffset={scrollOffset}
        dataLength={categoryPairList.length}
      />
    </S.ListContainer>
  );
}

export default memo(CategoriesList);
